import { FC, useRef, KeyboardEvent, useState } from 'react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import { Box, IconButton } from '@mui/material';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import TextureIcon from '@mui/icons-material/Texture';
import LinkIcon from '@mui/icons-material/Link';
import { TMarkdownTextEditorProps } from './types';

export const MarkdownTextEditor: FC<TMarkdownTextEditorProps> = ({ setText, text }) => {
	const textareaRef = useRef<any>();
	const [unorderedListMode, setUnorderedListMode] = useState(false);
	const [orderedListMode, setOrderedListMode] = useState(false);
	const [orderedListCounter, setOrderedListCounter] = useState(0);

	const [enterEnter, setEnterEnter] = useState(false);

	const setBoldHandler = () => {
		if (textareaRef?.current?.selectionStart === textareaRef?.current?.selectionEnd) {
			textareaRef.current.setRangeText(`**`);
			textareaRef.current.selectionStart += 1;
			textareaRef.current.focus();
			return;
		}
		const selected: string = textareaRef.current.value.slice(
			textareaRef.current.selectionStart,
			textareaRef.current.selectionEnd,
		);
		textareaRef.current.setRangeText(`*${selected}*`);
		textareaRef.current.selectionEnd += 1;
		textareaRef.current.selectionStart = textareaRef.current.selectionEnd;
		textareaRef.current.focus();

		setText(textareaRef.current.value);
	};

	const setItalicHandler = () => {
		if (textareaRef?.current?.selectionStart === textareaRef?.current?.selectionEnd) {
			textareaRef.current.setRangeText(`__`);
			textareaRef.current.selectionStart += 1;
			textareaRef.current.focus();
			return;
		}
		const selected: string = textareaRef.current.value.slice(
			textareaRef.current.selectionStart,
			textareaRef.current.selectionEnd,
		);
		textareaRef.current.setRangeText(`_${selected}_`);
		textareaRef.current.selectionEnd += 1;
		textareaRef.current.selectionStart = textareaRef.current.selectionEnd;
		textareaRef.current.focus();

		setText(textareaRef.current.value);
	};

	const setUnderlineHandler = () => {
		if (textareaRef?.current?.selectionStart === textareaRef?.current?.selectionEnd) {
			textareaRef.current.setRangeText(`___`);
			textareaRef.current.selectionStart += 2;
			textareaRef.current.focus();
			return;
		}
		const selected: string = textareaRef.current.value.slice(
			textareaRef.current.selectionStart,
			textareaRef.current.selectionEnd,
		);
		textareaRef.current.setRangeText(`__${selected}__`);
		textareaRef.current.selectionEnd += 1;
		textareaRef.current.selectionStart = textareaRef.current.selectionEnd;
		textareaRef.current.focus();
		setText(textareaRef.current.value);
	};

	const setStrikethroughHandler = () => {
		if (textareaRef?.current?.selectionStart === textareaRef?.current?.selectionEnd) {
			textareaRef.current.setRangeText(`~~`);
			textareaRef.current.selectionStart += 2;
			textareaRef.current.focus();
			return;
		}
		const selected: string = textareaRef.current.value.slice(
			textareaRef.current.selectionStart,
			textareaRef.current.selectionEnd,
		);
		textareaRef.current.setRangeText(`~${selected}~`);
		textareaRef.current.selectionEnd += 1;
		textareaRef.current.selectionStart = textareaRef.current.selectionEnd;
		textareaRef.current.focus();

		setText(textareaRef.current.value);
	};

	const setSpoilerHandler = () => {
		if (textareaRef?.current?.selectionStart === textareaRef?.current?.selectionEnd) {
			textareaRef.current.setRangeText(`||||`);
			textareaRef.current.selectionStart += 2;
			textareaRef.current.focus();
			return;
		}
		const selected: string = textareaRef.current.value.slice(
			textareaRef.current.selectionStart,
			textareaRef.current.selectionEnd,
		);
		textareaRef.current.setRangeText(`||${selected}||`);
		textareaRef.current.selectionEnd += 1;
		textareaRef.current.selectionStart = textareaRef.current.selectionEnd;
		textareaRef.current.focus();

		setText(textareaRef.current.value);
	};

	const setBlockQuotesHandler = () => {
		if (textareaRef?.current?.selectionStart === textareaRef?.current?.selectionEnd) {
			textareaRef.current.setRangeText(`\n>`);
			textareaRef.current.selectionStart += 2;
			textareaRef.current.focus();
			return;
		}
		const selected: string = textareaRef.current.value.slice(
			textareaRef.current.selectionStart,
			textareaRef.current.selectionEnd,
		);
		textareaRef.current.setRangeText(`\n>${selected}`);
		textareaRef.current.selectionStart = textareaRef.current.selectionEnd;
		textareaRef.current.focus();

		setText(textareaRef.current.value);
	};

	const setLinkHandler = () => {
		if (textareaRef?.current?.selectionStart === textareaRef?.current?.selectionEnd) {
			textareaRef.current.setRangeText(`[]()`);
			textareaRef.current.selectionStart += 1;
			textareaRef.current.focus();
			return;
		}
		const selected: string = textareaRef.current.value.slice(
			textareaRef.current.selectionStart,
			textareaRef.current.selectionEnd,
		);
		textareaRef.current.setRangeText(`[${selected}]()`);
		textareaRef.current.selectionEnd -= 1;
		textareaRef.current.selectionStart = textareaRef.current.selectionEnd;
		textareaRef.current.focus();
		setText(textareaRef.current.value);
	};

	// eslint-disable-next-line consistent-return
	function handleKeyPress(e: KeyboardEvent<HTMLTextAreaElement>) {
		const { key } = e;
		if (unorderedListMode && key === 'Enter') {
			if (enterEnter) {
				setEnterEnter(false);
				setUnorderedListMode(false);
				textareaRef.current.selectionStart -= 2;
				textareaRef.current.setRangeText(`\n`);
			} else textareaRef.current.setRangeText(`\n- `);
			textareaRef.current.selectionStart += 3;
			e.preventDefault();
			textareaRef.current.focus();
			setEnterEnter(true);
		} else if (orderedListMode && key === 'Enter') {
			if (enterEnter) {
				setEnterEnter(false);
				setOrderedListMode(false);
				textareaRef.current.selectionStart -= 3;
				textareaRef.current.setRangeText(`\n`);
				setOrderedListCounter(0);
			} else setOrderedListCounter(orderedListCounter + 1);
			textareaRef.current.setRangeText(`\n${orderedListCounter + 2}. `);
			textareaRef.current.selectionStart += 4;
			e.preventDefault();
			textareaRef.current.focus();
			setEnterEnter(true);
		} else setEnterEnter(false);
	}

	const setUnorderedListHandler = () => {
		setUnorderedListMode(true);
		if (textareaRef?.current?.selectionStart === textareaRef?.current?.selectionEnd) {
			textareaRef.current.setRangeText(`\n- `);
			textareaRef.current.selectionStart += 2;
			textareaRef.current.focus();
			return;
		}
		const selected: string = textareaRef.current.value.slice(
			textareaRef.current.selectionStart,
			textareaRef.current.selectionEnd,
		);
		textareaRef.current.setRangeText(`- ${selected}`);
		textareaRef.current.selectionEnd += 2;
		textareaRef.current.selectionStart = textareaRef.current.selectionEnd;
		textareaRef.current.focus();

		setText(textareaRef.current.value);
	};

	const setOrderedListHandler = () => {
		setOrderedListMode(true);
		if (textareaRef?.current?.selectionStart === textareaRef?.current?.selectionEnd) {
			textareaRef.current.setRangeText(`\n1. `);
			textareaRef.current.selectionStart += 3;
			textareaRef.current.focus();
			return;
		}
		const selected: string = textareaRef.current.value.slice(
			textareaRef.current.selectionStart,
			textareaRef.current.selectionEnd,
		);
		textareaRef.current.setRangeText(`1. ${selected}`);
		textareaRef.current.selectionEnd += 3;
		textareaRef.current.selectionStart = textareaRef.current.selectionEnd;
		textareaRef.current.focus();

		setText(textareaRef.current.value);
	};

	return (
		<Box
			sx={{
				borderRadius: '12px',
				backgroundColor: '#bdd2df',
				padding: '0 10px',
				mt: 2,
			}}
		>
			<div className="input">
				<div
					className="input-wrapper"
					style={{
						position: 'relative',
					}}
				>
					<div
						className="textarea textarea--make-proposal"
						style={{
							position: 'relative',
						}}
					>
						<div className="textarea-edit">
							<IconButton color="primary" onClick={setBoldHandler}>
								<FormatBoldIcon />
							</IconButton>
							<IconButton color="primary" onClick={setItalicHandler}>
								<FormatItalicIcon />
							</IconButton>
							<IconButton color="primary" onClick={setUnderlineHandler}>
								<FormatUnderlinedIcon />
							</IconButton>
							<IconButton color="primary" onClick={setStrikethroughHandler}>
								<StrikethroughSIcon />
							</IconButton>
							{/* <IconButton sx={{ color: '#66cbff' }} onClick={setSpoilerHandler}>
								<TextureIcon />
							</IconButton> */}
							<IconButton color="primary" onClick={setLinkHandler}>
								<LinkIcon />
							</IconButton>
						</div>
						<textarea
							style={{
								width: 'calc(100% - 20px)',
								height: '10rem',
								border: '1px solid #ccc',
								borderRadius: '12px',
								padding: '10px',
								fontSize: '1rem',
								fontFamily: 'Roboto',
							}}
							ref={textareaRef}
							placeholder=""
							onChange={e => setText(e.target.value)}
							value={text}
							onKeyPress={e => handleKeyPress(e)}
						/>
					</div>
				</div>
			</div>
		</Box>
	);
};
